import React from 'react';
import { Timeline, Typography } from 'antd';
import type { Quotation } from '@/services/quotation.service';
import { displayQuotationRevisionLabel } from '@/utils/quotationRevisionPolicy';
import dayjs from 'dayjs';

const { Text } = Typography;

type SubmissionRow = {
  id: string;
  revision_number: number;
  display_revision?: string;
  status: string;
  role: string;
  sent_date: string | null;
  superseded_at: string | null;
};

type Props = {
  quotation: Quotation;
  family: Quotation[];
  submissionHistory?: SubmissionRow[];
};

export const QuotationRevisionTimeline: React.FC<Props> = ({
  quotation,
  family,
  submissionHistory = [],
}) => {
  const events: { label: string; color?: string }[] = [];

  for (const r of [...family].sort((a, b) => Number(a.revision_number) - Number(b.revision_number))) {
    const rev = displayQuotationRevisionLabel(r.revision_number);
    events.push({
      label: `${rev} created${r.revision_reason ? ` — ${r.revision_reason}` : ''}`,
      color: r.id === quotation.id ? 'blue' : 'gray',
    });
    if (r.approval_status === 'Approved') {
      events.push({ label: `${rev} approved`, color: 'green' });
    }
    if (r.sent_date) {
      events.push({
        label: `${rev} sent to customer (${dayjs(r.sent_date).format('DD MMM')})`,
        color: 'blue',
      });
    }
    if (r.superseded_at) {
      events.push({
        label: `${rev} superseded (${dayjs(r.superseded_at).format('DD MMM')})`,
        color: 'gray',
      });
    }
    if (r.status === 'Accepted' && r.acceptance_date) {
      events.push({
        label: `${rev} customer accepted (${dayjs(r.acceptance_date).format('DD MMM')})`,
        color: 'green',
      });
    }
  }

  if (events.length === 0) {
    return <Text type="secondary">No timeline events recorded yet.</Text>;
  }

  return (
    <Timeline
      items={events.map((e) => ({
        color: e.color,
        children: e.label,
      }))}
    />
  );
};

export default QuotationRevisionTimeline;
